import { Toaster } from "@/ui/primitives/toaster";
import { Toaster as Sonner } from "@/ui/primitives/sonner";
import { TooltipProvider } from "@/ui/primitives/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "@/lib/react-query/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/features/auth/AuthContext";
import ProtectedRoute from "@/features/auth/ProtectedRoute";
import Auth from "@/features/auth/AuthPage";
import ResetPassword from "@/features/auth/ResetPasswordPage";
import ConfirmEmail from "@/features/auth/ConfirmEmailPage";
import Today from "@/features/today/TodayPage";
import Activities from "@/features/activities/ActivitiesPage";
import Offers from "@/features/offers/OffersPage";
import Reservations from "@/features/reservations/ReservationsPage";
import Validate from "@/features/validate/ValidatePage";
import Settings from "@/features/settings/SettingsPage";
import NotFound from "@/app/NotFound";
import { AppErrorBoundary } from "@/app/AppErrorBoundary";
import { Loader2 } from "lucide-react";
const queryClient = createQueryClient();

function AuthRedirect() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return <Auth />;
}

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/auth" element={<AuthRedirect />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/confirm" element={<ConfirmEmail />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Today />
            </ProtectedRoute>
          }
        />
        <Route
          path="/validate"
          element={
            <ProtectedRoute>
              <Validate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activities"
          element={
            <ProtectedRoute>
              <Activities />
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers"
          element={
            <ProtectedRoute>
              <Offers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reservations"
          element={
            <ProtectedRoute>
              <Reservations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
